import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConstructionIcon from '@mui/icons-material/Construction';
import PaymentsIcon from '@mui/icons-material/Payments';
import SaveIcon from '@mui/icons-material/Save';
import { SMART_COLORS, smartCardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const FarrierProcedureAdd = () => {
  const navigate = useNavigate();
  const [procedure, setProcedure] = useState({
    procedure_name: '',
    description: '',
    price: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/farrier_procedures`, {
        ...procedure,
        price: procedure.price || 0,
      });
      navigate('/farrier-procedures');
    } catch (error) {
      console.error('Error adding farrier procedure:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          ...smartCardSx,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: SMART_COLORS.warningSoft,
              color: SMART_COLORS.warning,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <ConstructionIcon />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: SMART_COLORS.text }}>
              Add Farrier Procedure
            </Typography>
            <Typography sx={{ color: SMART_COLORS.muted }}>
              Define a reusable farrier service and its default accounting price.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/farrier-procedures')}
          sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
        >
          Back
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2.5, md: 3.5 } }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Procedure name"
              value={procedure.procedure_name}
              onChange={(e) => setProcedure({ ...procedure, procedure_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={procedure.description}
              onChange={(e) => setProcedure({ ...procedure, description: e.target.value })}
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              label="Price"
              type="number"
              value={procedure.price}
              onChange={(e) => setProcedure({ ...procedure, price: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentsIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">SAR</InputAdornment>,
              }}
            />
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/farrier-procedures')}
                sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<SaveIcon />}
                sx={{ bgcolor: SMART_COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: SMART_COLORS.goldHover } }}
              >
                Save procedure
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default FarrierProcedureAdd;
